class ProductForm extends HTMLElement {
    constructor() {
        super();

        this.form = this.querySelector('form');
        this.sectionId = this.dataset.section;
        this.variantData = JSON.parse(document.getElementById(`ProductVariants-${this.sectionId}`).textContent);
        this.submitButton = this.querySelector('[type="submit"]');

        this.addEventListener('change', this.onVariantChange);
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        // Check for "added" parameter on load (Fallback Success State)
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('added') === 'true') {
            this.showNotification();
            // Clean URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        }
    }

    showNotification() {
        const notification = document.getElementById('cart-notification');
        if (notification) {
            notification.classList.add('show');
            // Scroll to top to ensure visibility
            notification.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    onSubmitHandler(evt) {
        evt.preventDefault();

        const submitButton = this.submitButton;
        if (submitButton.getAttribute('aria-disabled') === 'true') return;

        // Check if variant is available
        if (!this.currentVariant || !this.currentVariant.available) {
            alert('This product is currently out of stock.');
            return;
        }

        submitButton.setAttribute('aria-disabled', true);
        const originalText = submitButton.textContent;
        submitButton.textContent = 'Adding...';

        // Get the variant ID and quantity
        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1');

        // Check current cart to see if we already have this variant
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                // Find if this variant is already in cart
                const existingItem = cart.items.find(item => item.variant_id == variantId);
                const currentQtyInCart = existingItem ? existingItem.quantity : 0;
                const totalQty = currentQtyInCart + quantity;

                // Check against available inventory
                const availableStock = this.currentVariant.inventory_quantity;
                const inventoryManagement = this.currentVariant.inventory_management;

                // Only check stock if inventory is being tracked
                if (inventoryManagement && availableStock !== null && totalQty > availableStock) {
                    submitButton.removeAttribute('aria-disabled');
                    submitButton.textContent = originalText;

                    if (currentQtyInCart > 0) {
                        alert(`You already have ${currentQtyInCart} of this item in your cart. Only ${availableStock} available in stock.`);
                    } else {
                        alert(`Only ${availableStock} available in stock.`);
                    }
                    return;
                }

                // Proceed with adding to cart
                return fetch('/cart/add.js', {
                    method: 'POST',
                    body: formData
                });
            })
            .then(response => {
                if (!response) return; // Stock check failed
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                if (!data) return; // Stock check failed

                // Success - Update cart count and open drawer
                this.updateCartCount();

                // Open cart drawer
                if (window.openCartDrawer) {
                    window.openCartDrawer();
                }

                submitButton.textContent = 'Added!';
                setTimeout(() => {
                    submitButton.removeAttribute('aria-disabled');
                    submitButton.textContent = originalText;
                }, 2000);
            })
            .catch((e) => {
                console.error('Error adding to cart:', e);

                // Fallback: Use standard form submission with redirect to cart
                const returnInput = document.createElement('input');
                returnInput.type = 'hidden';
                returnInput.name = 'return_to';
                returnInput.value = '/cart';
                this.form.appendChild(returnInput);

                this.form.submit();
            });
    }

    updateCartCount() {
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                const cartCountElements = document.querySelectorAll('.header__cart-count, #cart-count');
                cartCountElements.forEach(el => {
                    el.textContent = cart.item_count;
                });
                // Also open cart drawer if it exists? 
                // For now, just update count as requested.
            });
    }

    onVariantChange() {
        this.updateOptions();
        this.updateMasterId();
        this.updatePrice();
        this.updatePickupAvailability();
        this.toggleAddButton(true, '', false);

        if (!this.currentVariant) {
            this.toggleAddButton(true, '', true);
            this.setUnavailable();
        } else {
            this.updateMedia();
            this.updateURL();
            this.updateVariantInput();
            this.renderProductInfo();
        }
    }

    updateOptions() {
        this.options = Array.from(this.querySelectorAll('select, input[type="radio"]:checked'), (element) => element.value);
    }

    updateMasterId() {
        this.currentVariant = this.getVariantData().find((variant) => {
            return !variant.options.map((option, index) => {
                return this.options[index] === option;
            }).includes(false);
        });
    }

    updateMedia() {
        if (!this.currentVariant || !this.currentVariant.featured_media) return;
        // Implementation for scrolling to media could go here
        // For now we skip as it's a grid layout
    }

    updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
        const input = this.form.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updatePrice() {
        if (!this.currentVariant) return;

        // Simplified price update - assumes standard Shopify currency formatting
        // Ideally we would use the Money API or Liquid rendering

        const priceContainer = document.querySelector('.product__price-container .price');
        if (!priceContainer) return;

        // Reset classes
        priceContainer.classList.remove('price--on-sale');

        const regularPrice = (this.currentVariant.price / 100).toFixed(2);

        if (this.currentVariant.compare_at_price > this.currentVariant.price) {
            priceContainer.classList.add('price--on-sale');
            const comparePrice = (this.currentVariant.compare_at_price / 100).toFixed(2);

            // Assuming GBP for Thanga.co.uk
            priceContainer.innerHTML = `
            <span class="price-item price-item--sale">£${regularPrice}</span>
            <span class="price-item price-item--regular" style="text-decoration: line-through; color: #999; margin-left:10px;">£${comparePrice}</span>
        `;
        } else {
            priceContainer.innerHTML = `<span class="price-item price-item--regular">£${regularPrice}</span>`;
        }
    }

    renderProductInfo() {
        this.toggleAddButton(!this.currentVariant.available, this.currentVariant.available ? 'Add to Basket' : 'Sold Out');
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
        const dynamicButtons = this.querySelector('.shopify-payment-button');

        if (disable) {
            this.submitButton.setAttribute('disabled', 'disabled');
            if (text) this.submitButton.textContent = text;
            if (dynamicButtons) dynamicButtons.style.display = 'none';
        } else {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.textContent = text;
            if (dynamicButtons) dynamicButtons.style.display = 'block';
        }
    }

    setUnavailable() {
        this.submitButton.textContent = 'UNAVAILABLE';
        this.submitButton.setAttribute('disabled', 'disabled');
    }

    getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
    }
}

if (!customElements.get('product-form')) {
    customElements.define('product-form', ProductForm);
}
